// video-precheck.ts — 上传前用 mp4box.js 解析视频 moov,做编码白名单预校验。
//
// spec: docs/ai/project-design/02-file-storage.md §3.4
//   - 容器:mp4
//   - 编码:h264 / h265 / av1
//   - h265 必须 hvc1(不接受 hev1)
//   - 不符合 → 直接拒绝(不发起上传,省整轮带宽)
//
// 校验结果(VideoPreCheckResult)同时回传给 /gapi/user/upload/big/init 的 `video_pre_check` 字段,
// 由后端 media.ValidateVideoPreCheck 二次校验(防绕过;前端校验仅 UX 提前拦截)。

import { createFile, type MP4ArrayBuffer, type MP4Info } from 'mp4box'

// VideoPreCheckResult 与后端 upload_big_handlers.go 的 video_pre_check JSON 字段对齐
export type VideoPreCheckResult = {
  container: string
  video_codec: string
  video_codec_tag: string
}

// VideoPreCheckError 携带用户友好的中文提示(由 uploadFile onError → toast 消费)
export class VideoPreCheckError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'VideoPreCheckError'
  }
}

const ALLOWED = new Set(['h264', 'h265', 'av1'])

// mapCodec 把 mp4box 的 track.codec(如 "hvc1.1.6.L93.B0")映射为
// { codec: h264/h265/av1/'', tag: 四字符 fourCC }。
function mapCodec(raw: string): { codec: string; tag: string } {
  const fourcc = (raw.split('.')[0] || '').toLowerCase().trim()
  switch (fourcc) {
    case 'avc1':
    case 'avc3':
      return { codec: 'h264', tag: fourcc }
    case 'hvc1':
      return { codec: 'h265', tag: 'hvc1' }
    case 'hev1':
      return { codec: 'h265', tag: 'hev1' }
    case 'av01':
      return { codec: 'av1', tag: fourcc }
    default:
      return { codec: '', tag: fourcc }
  }
}

// detectContainer 用 ftyp brands 区分 mp4 vs mov(QuickTime)。
function detectContainer(info: MP4Info): string {
  const brands = (info.brands || []).map((b) => b.toLowerCase().trim())
  if (brands.includes('qt')) return 'mov'
  return 'mp4'
}

// parseMoov 流式喂 mp4box 直到 onReady(moov 解析完);faststart 文件 moov 在头部,
// 只需读前几 MB 即触发,无需读完整文件。非 faststart 文件 moov 在尾部 → 读到 EOF 后 flush。
const CHUNK_SIZE = 4 * 1024 * 1024 // 4MB

function parseMoov(file: File): Promise<MP4Info> {
  return new Promise<MP4Info>((resolve, reject) => {
    const mp4 = createFile()
    let done = false
    mp4.onReady = (info) => {
      if (done) return
      done = true
      mp4.stop()
      resolve(info)
    }
    mp4.onError = (e) => {
      if (done) return
      done = true
      reject(new VideoPreCheckError(`无法解析视频文件(可能不是有效的 mp4):${e}`))
    }

    let offset = 0
    const pump = async () => {
      try {
        while (!done && offset < file.size) {
          const end = Math.min(offset + CHUNK_SIZE, file.size)
          const buf = (await file.slice(offset, end).arrayBuffer()) as MP4ArrayBuffer
          buf.fileStart = offset
          mp4.appendBuffer(buf)
          offset = end
        }
        if (!done) {
          mp4.flush()
          // flush 后仍未 onReady → 没找到 moov 视频轨
          if (!done) {
            done = true
            reject(new VideoPreCheckError('未在视频文件中找到视频轨(moov)'))
          }
        }
      } catch (err) {
        if (!done) {
          done = true
          reject(err instanceof Error ? err : new VideoPreCheckError(String(err)))
        }
      }
    }
    void pump()
  })
}

// precheckVideo 解析 + 白名单校验;通过返回 VideoPreCheckResult,不通过 throw VideoPreCheckError。
// 与后端 media.ValidateVideoPreCheck 逻辑一致(mp4 + h264/h265/av1 + h265→hvc1)。
export async function precheckVideo(file: File): Promise<VideoPreCheckResult> {
  const info = await parseMoov(file)
  const track = info.videoTracks?.[0]
  if (!track) {
    throw new VideoPreCheckError('该文件不含视频轨,无法作为视频上传')
  }

  const container = detectContainer(info)
  if (container !== 'mp4') {
    throw new VideoPreCheckError('视频容器必须是 mp4(不支持 mov / 其他格式)')
  }

  const { codec, tag } = mapCodec(track.codec)
  if (!ALLOWED.has(codec)) {
    throw new VideoPreCheckError(
      `视频编码不被支持(${track.codec});仅允许 H.264 / H.265(hvc1)/ AV1`,
    )
  }
  if (codec === 'h265' && tag !== 'hvc1') {
    throw new VideoPreCheckError('H.265 视频必须为 hvc1 标签(不支持 hev1)')
  }

  return { container, video_codec: codec, video_codec_tag: tag }
}
